import React, { useCallback, useEffect, useState } from "react";
import { Configuration, IPublicClientApplication, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";

import { msalConfig } from "authConfig";
import { LoaderPage } from "pages";
import { useAuthActions } from "store";
import { SSOContext } from "./SSOContext";

export const SSOProvider: React.FC<{ children: React.ReactNode }> = ({ children }): JSX.Element => {
  const { checkSSOAction } = useAuthActions();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [azureMsalInstance, setAzureMsalInstance] = useState<IPublicClientApplication | null>(null);
  const [isSamlEnabled, setIsSamlEnabled] = useState<boolean>(false);

  const [ssoOnly, setSsoOnly] = useState<boolean>(false);

  const checkSSO = useCallback(async () => {
    try {
      const { saml_sso_enabled, azure_sso_enabled, sso_app_id, sso_app_authority, sso_only } =
        await checkSSOAction();
      setSsoOnly(sso_only ?? false);
      if (azure_sso_enabled && sso_app_id && sso_app_authority) {
        const config: Configuration = {
          ...msalConfig,
          auth: {
            ...msalConfig.auth,
            clientId: sso_app_id,
            authority: sso_app_authority
          }
        };
        setAzureMsalInstance(new PublicClientApplication(config));
      }
      if (saml_sso_enabled) {
        setIsSamlEnabled(true);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSSO();
  }, []);

  if (isLoading) {
    return <LoaderPage loading={isLoading} />;
  }

  return (
    <SSOContext.Provider
      value={{
        saml_sso_enabled: isSamlEnabled,
        azure_sso_enabled: Boolean(azureMsalInstance),
        sso_only: ssoOnly
      }}
    >
      {azureMsalInstance ? <MsalProvider instance={azureMsalInstance!}>{children}</MsalProvider> : children}
    </SSOContext.Provider>
  );
};
